import { loadCssTheme } from '@whistler-ui/k2/styles/themes';
import { useEffect, useLayoutEffect } from 'react';

import { useThemeStore } from '../store/theme';
import { isThemeId } from '../store/theme/brands';

const THEME_SWITCH_EVENT = 'whistler:switch-theme';

// Moved verbatim from the original context-based theme setup so the
// dev-only theme switcher keeps behaving exactly as it did before this slice.
function isDevEnv(): boolean {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return (
    import.meta.env.DEV ||
    host === 'localhost' ||
    host.endsWith('-dev.netlync-int.com') ||
    host.endsWith('-ci.netlync-int.com')
  );
}

export const useThemeSetup = (): void => {
  const { themeId, config } = useThemeStore();
  // Depend on the image URL, not on `config`: the brand config is a fresh
  // reference after every `setTheme`, while the URL stays stable per brand.
  const backgroundLoginImage = config.assets.backgroundLoginImage;

  useLayoutEffect(() => {
    loadCssTheme(themeId);
    document.documentElement.style.setProperty(
      '--background-image-login-page',
      `url(${backgroundLoginImage})`,
    );
  }, [themeId, backgroundLoginImage]);

  useEffect(() => {
    if (!isDevEnv()) {
      return undefined;
    }

    const handler = (event: Event): void => {
      const next = (event as CustomEvent).detail?.themeId;
      if (isThemeId(next)) {
        useThemeStore.getState().setTheme(next);
      }
    };

    window.addEventListener(THEME_SWITCH_EVENT, handler);
    return () => window.removeEventListener(THEME_SWITCH_EVENT, handler);
  }, []);
};
